import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ROLE_NAMES, fmtDateTime } from '@tww/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Checkbox, TextArea, TextInput } from '../ui/fields';
import { Note, Panel } from '../ui/basics';
import { ConfirmDialog } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';

export function ProfilePage() {
  const { me, refresh } = useAuth();
  const toast = useToast();
  const [initials, setInitials] = useState(me?.user.initials ?? '');
  const [digest, setDigest] = useState(me?.user.daily_digest ?? false);
  const [signature, setSignature] = useState(me?.user.signature ?? '');
  const [sp, setSp] = useSearchParams();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  useEffect(() => {
    const g = sp.get('graph');
    if (!g) return;
    toast(g === 'ok' ? 'Zustimmung für Microsoft 365 gespeichert' : 'Zustimmung fehlgeschlagen, bitte erneut versuchen', g === 'ok' ? 'ok' : 'error');
    void refresh();
    setSp({}, { replace: true });
  }, [sp]);
  const revoke = useMutation({
    mutationFn: () => api.delete('/me/graph'),
    onSuccess: async () => {
      await refresh();
      setConfirmRevoke(false);
      toast('Zustimmung widerrufen');
    },
    onError: (e: Error) => {
      setConfirmRevoke(false);
      toast(e.message, 'error');
    },
  });
  const graph = me?.graph;
  const save = useMutation({
    mutationFn: () => api.patch('/auth/me', { initials: initials || null, daily_digest: digest, signature: signature.trim() || null }),
    onSuccess: async () => {
      await refresh();
      toast('Gespeichert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <>
      <div className="head">
        <div>
          <h1>Profil</h1>
          <div className="sub">
            {me?.user.email} · {me?.roles.map((r) => ROLE_NAMES[r]).join(', ') || 'keine Rolle'}
          </div>
        </div>
      </div>
      <Panel title="Einstellungen">
        <div className="pb">
          <div className="form">
            <TextInput label="Kürzel" value={initials} onChange={(v) => setInitials(v.toUpperCase().slice(0, 5))} hint="z. B. KMS" />
            <Checkbox value={digest} onChange={setDigest} label="Tagesmail mit Erinnerungen" hint="Jeden Morgen eine E-Mail mit deinen offenen Punkten" />
            <TextArea label="E-Mail-Signatur" value={signature} onChange={setSignature} rows={4} placeholder={`${me?.user.display_name ?? ''}\n${me?.roles[0] ? ROLE_NAMES[me.roles[0]] + ', ' : ''}Trinkwasserwald e.V.`} hint="Wird unter „Mit freundlichen Grüßen“ in E-Mails aus dem System eingesetzt; leer = Name und Rolle" />
          </div>
          <div className="profile-actions">
            <button className="btn primary" type="button" onClick={() => save.mutate()} disabled={save.isPending}>
              Speichern
            </button>
          </div>
        </div>
      </Panel>
      <Panel title="E-Mail-Versand über Microsoft 365">
        <div className="pb">
          {!graph?.configured ? (
            <Note kind="info">Der Versand über Microsoft 365 ist noch nicht eingerichtet. E-Mails gehen bis dahin über das Sammelpostfach oder werden in Outlook geöffnet.</Note>
          ) : graph.consented ? (
            <>
              <Note kind="good">Zustimmung erteilt{graph.consented_at ? ` am ${fmtDateTime(graph.consented_at)}` : ''}. E-Mails aus dem System werden in deinem Namen gesendet und liegen in Outlook unter „Gesendet“.</Note>
              <div className="profile-actions">
                <button type="button" className="btn" onClick={() => setConfirmRevoke(true)} disabled={revoke.isPending}>
                  Zustimmung widerrufen
                </button>
              </div>
            </>
          ) : (
            <>
              <Note>Damit E-Mails aus dem System in deinem Namen gesendet werden und in Outlook unter „Gesendet“ liegen, braucht die Datenbank einmalig deine Zustimmung bei Microsoft.</Note>
              <div className="profile-actions">
                <a className="btn primary" href="/api/v1/auth/graph/consent?next=/profil">
                  <Icon name="mail" /> Zustimmung bei Microsoft erteilen
                </a>
              </div>
            </>
          )}
        </div>
      </Panel>
      {confirmRevoke && (
        <ConfirmDialog
          title="Zustimmung widerrufen"
          text="E-Mails aus dem System gehen danach nicht mehr in deinem Namen raus, sondern über das Sammelpostfach oder werden in Outlook geöffnet. Du kannst die Zustimmung jederzeit neu erteilen."
          confirmLabel="Widerrufen"
          danger
          onConfirm={() => revoke.mutate()}
          onClose={() => setConfirmRevoke(false)}
          busy={revoke.isPending}
        />
      )}
    </>
  );
}
